import {ReactElement, useRef} from "react"
import utils from "./utils"

type Props<T extends string> = {
  options: ReadonlyMap<T, string>
  value: ReadonlyArray<T>
  onChange: (newValue: ReadonlyArray<T>) => void
  className?: string
  noSelectionContent?: string
}
export const SEPARATOR = ":::" as const

export function getKeyRoot(key: string): string {
  const index = key.lastIndexOf(SEPARATOR)
  return key.slice(0, index === -1 ? undefined : index)
}

export function getKeyExtension(key: string): string {
  const index = key.lastIndexOf(SEPARATOR)
  return index === -1 ? "" : key.slice(index + SEPARATOR.length)
}

export default function MultiSelectInOrder<T extends string>(
  {options, value, onChange, className, noSelectionContent}: Props<T>
): ReactElement {
  /**
   * Creates multiple select elements, so that a multi select can be done
   * where order is maintained.
   * @property options: a map where the keys are the select values, and the values
   * are the content. Note that the empty string is not allowed as a key
   * @property value: an array of options keys that indicates the current selection
   * @property onChange: callback function, receiving the current selection
   * @property className: The classname of the surrounding span element
   * @noSelectionContent: The option content for the "nothing selected" option.
   * Defaults to "---"
   *
   * Note that each subsequent select dropdown will not contain any of the values
   * selected in the previous selects. This match is done the string minus any part
   * past the last :::. So hence if you have two keys, one "orderDate:::ASC" and
   * one "orderDate:::DESC", selecting one will remove the other from subsequent
   * selects.
   **/
  if ([...options.keys()].some(key => key === "")) {
    throw new Error("The empty string cannot be used as an option key")
  }
  const activeValues = [...new Set(value)].filter(v => options.has(v))
  const localOptions = new Map(options.entries())
  const nrSelects = Math.min(activeValues.length + 1, localOptions.size)
  const containingSpan = useRef<HTMLSpanElement>(null)
  function onChangeSingleSelect() {
    if (!containingSpan.current) {
      return
    }
    const selects = [...containingSpan.current.querySelectorAll("select")]
    const values = selects
      .map(s => (s.querySelector(":checked") as HTMLOptionElement)?.value)
      .filter(val => val !== "")
    onChange(values.filter((val, index) =>
      values.findIndex(val2 => getKeyRoot(val2) === getKeyRoot(val)) === index
    ) as T[])
  }
  return <span className={className} ref={containingSpan}>
    {utils.range(nrSelects).map(index => {
      const selected: T | undefined = activeValues[index]
      const result =
        <select key={index} value={selected ?? ""} onChange={onChangeSingleSelect}>
          <option value="">{noSelectionContent ?? "---"}</option>
          {[...localOptions.entries()].map(
            ([value, content]) => <option key={value} value={value}>{content}</option>
          )}
        </select>
        if (selected) {
          const root = getKeyRoot(selected)
          localOptions.forEach((_, key) => {
            if (getKeyRoot(key) === root) {
              localOptions.delete(key)
            }
          })
        }
        return result
    })}
    <button disabled={activeValues.length === 0} onClick={() => onChange([])}>
      reset
    </button>
  </span>
}
